import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [needsSetup, setNeedsSetup] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    useEffect(() => {
        // Check if initial setup is needed
        const checkSetup = async () => {
            try {
                const response = await api.get('/auth/check-setup');
                setNeedsSetup(response.data.needs_setup);
            } catch (error) {
                console.error('Setup check failed:', error);
            }
        };
        checkSetup();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(formData.email, formData.password);
            toast.success('Connexion réussie');
            navigate('/');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur de connexion';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-split">
            {/* Form Side */}
            <div className="auth-form-side bg-background">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center mb-4">
                            <img src="/bbm-logo.png" alt="BBM" className="w-20 h-20 rounded-[2px] object-cover" />
                        </div>
                        <h1 className="text-3xl font-semibold tracking-tight">BBM</h1>
                        <p className="text-muted-foreground mt-2">
                            Connectez-vous à votre compte
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="input-luxury"
                                required
                                data-testid="email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="input-luxury pr-10"
                                    required
                                    data-testid="password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    data-testid="toggle-password-btn"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full btn-luxury"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <span className="spinner" />
                                    Connexion...
                                </span>
                            ) : (
                                'Se Connecter'
                            )}
                        </Button>
                    </form>

                    {needsSetup && (
                        <p className="text-center text-sm text-muted-foreground">
                            Première utilisation ?{' '}
                            <Link
                                to="/inscription"
                                className="text-primary hover:underline font-medium"
                                data-testid="register-link"
                            >
                                Créer le compte administrateur
                            </Link>
                        </p>
                    )}
                </div>
            </div>

            {/* Image Side */}
            <div
                className="auth-image-side"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1600024914956-202df0a5acad?w=1200)',
                }}
            >
                <div className="auth-image-overlay">
                    <blockquote className="text-white">
                        <p className="text-2xl font-light italic mb-4">
                            "Le parfum est la forme la plus intense du souvenir."
                        </p>
                        <footer className="text-white/70 text-sm">
                            — Jean Paul Guerlain
                        </footer>
                    </blockquote>
                </div>
            </div>
        </div>
    );
}
